import { useEffect, useState } from "react";

import { useInspectionAttributes } from "../../../hooks/query";
import { defaultEstimatingFormState, type EstimatingFormState } from "../types";
import {
    buildEstimatingAttributeIds,
    parseEstimatingFromAttributes,
} from "../utils/estimatingAttributes";

export default function useEstimatingInit(projectId?: string) {
    const [estimating, setEstimating] = useState<EstimatingFormState>(
        defaultEstimatingFormState,
    );
    const [attributeIds, setAttributeIds] = useState<Record<string, string>>(
        {},
    );
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { useListByProjectId } = useInspectionAttributes();
    const listAttributesSvc = useListByProjectId();

    const loadEstimatingData = async (
        id: string,
        isCancelled?: () => boolean,
    ) => {
        setLoading(true);
        setError(null);

        try {
            const attributesRes = await listAttributesSvc.mutateAsync({
                ProjectId: id,
            });
            if (isCancelled?.()) return { success: false };

            const attributes = Array.isArray(attributesRes) ? attributesRes : [];

            setEstimating(parseEstimatingFromAttributes(attributes));
            setAttributeIds(buildEstimatingAttributeIds(attributes));

            return { success: true };
        } catch (err: any) {
            if (!isCancelled?.()) {
                setError(err.message || "Failed to load estimating settings");
            }
            return { success: false, error: err };
        } finally {
            if (!isCancelled?.()) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        if (!projectId) return;

        let cancelled = false;
        loadEstimatingData(projectId, () => cancelled);

        return () => {
            cancelled = true;
        };
    }, [projectId]);

    return {
        estimating,
        setEstimating,
        attributeIds,
        setAttributeIds,
        loading,
        error,
        loadEstimatingData,
    };
}
